/**
 * Full-width list of studio sounds with their actions (play, rename, share, add to beat, download, delete) — used for
 * a job's results (e.g. the trend drawer). "Recent creations" uses the compact rows in SoundRows.tsx.
 */
import { Download, Globe, Link2, Lock, MoreHorizontal, Pencil, Plus, Share2, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/components/ui/utils';
import type { StudioTrack } from '@/api/audioStudioApi';
import { PlayButton } from './AudioPlayer';
import { RenameInline } from './SoundRows';
import { SOURCE_LABEL, VISIBILITY_LABEL, capitalize, formatClock, formatDate, studioPlayable } from './format';
import { useTrackActions } from './useTrackActions';

const SOURCE_TONE: Record<string, string> = {
  prompt: 'bg-[#f4ebff] text-[#6941c6]',
  preset: 'bg-[#eff8ff] text-[#175cd3]',
  trend_lookalike: 'bg-[#fdf2fa] text-[#c11574]',
  library: 'bg-[#f2f4f7] text-[#344054]',
};

export function SourceBadge({ source }: { source: StudioTrack['source'] }) {
  return <span className={cn('rounded-full px-2 py-0.5 text-[10.5px]', SOURCE_TONE[source] || SOURCE_TONE.library)} style={{ fontWeight: 600 }}>{SOURCE_LABEL[source] || source}</span>;
}

export function VisibilityBadge({ track }: { track: StudioTrack }) {
  const Icon = track.visibility === 'private' ? Lock : track.visibility === 'members' ? Users : Globe;
  const label = track.visibility === 'members' && track.allowed_user_ids.length
    ? `${track.allowed_user_ids.length} member${track.allowed_user_ids.length === 1 ? '' : 's'}`
    : VISIBILITY_LABEL[track.visibility] || 'All team';
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#eaecf0] px-2 py-0.5 text-[10.5px] text-[#475467]" style={{ fontWeight: 500 }}>
      <Icon className="h-3 w-3" aria-hidden /> {label}
      {track.share_enabled && <Link2 className="ml-0.5 h-3 w-3 text-[#7c3aed]" aria-label="Public link on" />}
    </span>
  );
}

export function StudioTrackList({ tracks, showOwner = true, emptyText }: { tracks: StudioTrack[]; showOwner?: boolean; emptyText?: string }) {
  const actions = useTrackActions();

  if (!tracks.length) return emptyText ? <div className="px-4 py-10 text-center text-[13px] text-[#98a2b3]">{emptyText}</div> : null;

  return (
    <>
      <ul className="divide-y divide-[#f2f4f7]">
        {tracks.map((t) => (
          <li key={t.track_id} className="flex flex-wrap items-center gap-3 px-3 py-3 sm:flex-nowrap sm:px-4">
            <PlayButton item={studioPlayable(t)} coverUrl={t.cover_url} />
            <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
              {actions.renamingId === t.track_id ? (
                <RenameInline track={t} onDone={actions.stopRename} />
              ) : (
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{t.title || 'Untitled'}</span>
                  {t.is_owner && (
                    <button type="button" onClick={() => actions.startRename(t)} aria-label={`Rename ${t.title}`} className="shrink-0 rounded p-0.5 text-[#98a2b3] hover:text-[#344054]"><Pencil className="h-3.5 w-3.5" aria-hidden /></button>
                  )}
                </div>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-[#667085]">
                <SourceBadge source={t.source} />
                <VisibilityBadge track={t} />
                {[capitalize(t.genre), capitalize(t.mood), t.duration_s ? formatClock(t.duration_s) : null].filter(Boolean).map((x) => <span key={x as string}>{x}</span>)}
                {showOwner && <span className="truncate">· {t.is_owner ? 'You' : t.owner_name || 'Team member'}</span>}
                <span>· {formatDate(t.created_at)}</span>
              </div>
              {t.trend_ref && <div className="mt-0.5 truncate text-[11px] text-[#98a2b3]">Like “{t.trend_ref.title}”</div>}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={() => actions.attach(t)} className="h-8 gap-1 text-[12px]" aria-label={`Add ${t.title || 'sound'} to a beat`}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> <span className="hidden sm:inline">Add to beat</span><span className="sm:hidden">Beat</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => actions.share(t)} className="h-8 gap-1 text-[12px]" aria-label={`Share ${t.title}`}>
                <Share2 className="h-3.5 w-3.5" aria-hidden /> <span className="hidden md:inline">Share</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" aria-label={`More actions for ${t.title}`} className="flex h-8 w-8 items-center justify-center rounded-md text-[#667085] hover:bg-[#f2f4f7]"><MoreHorizontal className="h-4 w-4" aria-hidden /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={!t.audio_url} onSelect={() => actions.download(t)}>
                    <Download className="h-4 w-4" aria-hidden /> Download
                  </DropdownMenuItem>
                  {t.is_owner && (
                    <DropdownMenuItem onSelect={() => actions.startRename(t)}><Pencil className="h-4 w-4" aria-hidden /> Rename</DropdownMenuItem>
                  )}
                  {t.is_owner && (
                    <DropdownMenuItem onSelect={() => actions.askDelete(t)} className="text-[#b42318] focus:text-[#b42318]"><Trash2 className="h-4 w-4" aria-hidden /> Delete</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        ))}
      </ul>
      {actions.dialogs}
    </>
  );
}
